import {
  AnonymityLevel,
  ResultsDeliveryType,
} from "@pollyslack/types/src/common";
import { NudgeMessageType, PollType } from "../../../../types";
import { IPollySchema } from "@pollyai/api-client-server";

/**
 * Replaces breaking spaces in a jsx string to non breaking spaces
 */
export function sanitizeJSXString(view: string): string {
  return view.replace(/\u00A0/g, " ");
}

/**
 * Returns the generated message string for a provided nudge message id.
 * @param messageName the string name of the nudge message being built
 */
export function getNudgeMessageString(messageName: NudgeMessageType): string {
  switch (messageName) {
    case NudgeMessageType.THREE_DAYS_AFTER_TRAIL:
      return "How's your trial going?";
    case NudgeMessageType.SEVEN_DAYS_AFTER_TRAIL:
      return "Your Polly trial is ending!";
    case NudgeMessageType.SEVEN_DAYS_AFTER_TRAIL_V2:
      return "Your Polly trial is ending!";
    case NudgeMessageType.SEVEN_DAYS_AFTER_TRAIL_ENDS:
      return "It's been a week since your trial ended!";
    case NudgeMessageType.SEVEN_DAYS_AFTER_TRAIL_ENDS_V2:
      return "It's been a week since your trial ended!";
    case NudgeMessageType.SEVEN_DAYS_AFTER_TRAIL_RESTART:
      return "Have another go, on us!";
    case NudgeMessageType.SEVEN_DAYS_AFTER_ONE_TIME_SUB_PURCHASE:
      return "Your 7-day Polly subscription is ending!";
    case NudgeMessageType.FOURTEEN_DAYS_AFTER_TRAIL_ENDS:
      return "Make your next polly pop!";
    case NudgeMessageType.THIRTY_FIVE_DAYS_AFTER_TRAIL_RESTART:
      return "Looking for better team insights?";
    case NudgeMessageType.SIXTY_THREE_DAYS_AFTER_TRAIL_RESTART:
      return "Make your presentations pop with Polly!";
    case NudgeMessageType.NINETY_ONE_DAYS_AFTER_TRAIL_RESTART:
      return "Engagement when you need it";
    case NudgeMessageType.CARD_EXPIRING:
      return "Your credit card is expiring!";
    case NudgeMessageType.DISABLE_TRIAL_TRACK_DM1:
      return "Your trial track will be disabled soon!";
    case NudgeMessageType.DISABLE_TRIAL_TRACK_DM2:
      return "Your trial track has been disabled";
    case NudgeMessageType.DISABLE_TRIAL_TRACK_DM3:
      return "Upgrade to re-enable your trial track";
    case NudgeMessageType.RETENTION_DM_60_DAYS:
      return "We're sad to see you go";
    case NudgeMessageType.RETENTION_DM_30_DAYS:
      return "30 days left before you lose access to these Polly features";
    case NudgeMessageType.RETENTION_DM_14_DAYS:
      return "Two weeks left in your Polly subscription";
    case NudgeMessageType.RETENTION_DM_7_DAYS:
      return "Don't lose access to your results history!";
    case NudgeMessageType.RETENTION_DM_1_DAY:
      return "Final Call! Premium Polly features will stop working in 1 day";
    case NudgeMessageType.SIX_DAYS_AFTER_TRIAL_START_GSHEET:
      return "Your Google Sheet will be disabled tomorrow!";
    case NudgeMessageType.TRIAL_DM_NOV2025_1:
      return "You have unlimited access to Polly templates";
    case NudgeMessageType.TRIAL_DM_PAIRINGS:
    case NudgeMessageType.NEW_ORG_DM_PAIRINGS:
      return "You've got Polly Pairings";
    case NudgeMessageType.ONBOARDING_DM_PAIRINGS:
      return "Polly Pairings: coffee chats and introductions in Slack";
    case NudgeMessageType.TRIAL_DM_NOV2025_2:
      return "Your Polly trial is ending today!";
    case NudgeMessageType.TRIAL_DM_NOV2025_3:
      return "Make your presentations more interactive with Polly!";
    case NudgeMessageType.TRIAL_DM_NOV2025_4:
      return "Have you tried Polly social games yet?";
    case NudgeMessageType.TRIAL_DM_NOV2025_5:
      return "Polly Quizzes are here!";
    case NudgeMessageType.TRIAL_DM_NOV2025_6:
      return "Make every presentation on-brand with Polly Themes!";
    case NudgeMessageType.TRIAL_DM_NOV2025_7:
      return "Word Cloud: Turn audience feedback into instant insights!";
    case NudgeMessageType.TRIAL_DM_NOV2025_8:
      return "Top 3 use cases for Polly Q&A";
    case NudgeMessageType.WIN_BACK_DM_30_DAYS:
      return "We miss you 😔";
    case NudgeMessageType.WIN_BACK_DM_60_DAYS:
      return "✨ Get unlimited access to Polly templates";
    case NudgeMessageType.WIN_BACK_DM_90_DAYS:
      return "💬 Turn your Google Slides or PowerPoint decks into interactive conversations!";
    case NudgeMessageType.WIN_BACK_DM_120_DAYS:
      return "Have you tried Polly social games yet? 🎉";
    case NudgeMessageType.ABANDONED_CART_DM_1:
      return "Deciding on a Polly plan?";
    case NudgeMessageType.ABANDONED_CART_DM_2:
      return "Did you see our templates library?";
    case NudgeMessageType.CELEBRATION_FIRST_CREATION:
      return "High five on your first Polly!";
    case NudgeMessageType.CELEBRATION_FIRST_RESPONSE:
      return "Great job on your first response!";
    case NudgeMessageType.BASIC_PRO_UPSELL_DM_1:
      return "You've been upgraded!";
    case NudgeMessageType.BASIC_PRO_UPSELL_DM_2:
      return "Your PRO plan promotion expires today";
    case NudgeMessageType.UNASSIGNED_LICENSE_DM:
      return "You have unassigned Polly licenses!";
    case NudgeMessageType.ORPHANED_POLLY_DM_1:
      return "Almost there..";
    case NudgeMessageType.ORPHANED_POLLY_DM_2:
      return "One step left";
    case NudgeMessageType.PAIRINGS_FUTURELIMIT_DM_1:
      return "Your team loves Pairings — time to make room for more";
    case NudgeMessageType.PAIRINGS_LIMIT_DM_1:
      return "Pairings is paused in your channel.";
    default:
      return "";
  }
}

/**
 * Returns boolean indicating whether to show the names of votes or not.
 * @param {AnonymityLevel} anonymityLevel to check if it's Public or Anonymized Results
 * @param {boolean} isAuthor whether the user viewing the results is author or not
 */

export function canShowVoterNames(
  anonymityLevel: AnonymityLevel,
  isAuthor: boolean,
  isSharing: boolean = false,
  isAuthorDM: boolean = false,
): boolean {
  if (
    isAuthorDM &&
    anonymityLevel !== AnonymityLevel.ANONYMOUS &&
    anonymityLevel !== AnonymityLevel.CONFIDENTIAL
  ) {
    return true;
  }

  if (isSharing && anonymityLevel !== AnonymityLevel.PUBLIC) {
    return false;
  }

  return (
    anonymityLevel === AnonymityLevel.PUBLIC ||
    (anonymityLevel === AnonymityLevel.ANONYMIZE_PARTICIPANT_RESULTS &&
      isAuthor)
  );
}

/**
 * Returns boolean indicating whether to show the usernames or not.
 * @param {AnonymityLevel} anonymityLevel to check if it's Anonymous, Confidential or Anonymized Results
 * @param {boolean} isAuthor whether the user viewing the results is author or not
 */

export function doNotShowUsername(
  anonymityLevel: AnonymityLevel,
  isAuthor: boolean,
  isSharing: boolean = false,
  isAuthorDM: boolean = false,
  isSharedWithUser: boolean = false,
): boolean {
  if (
    (isAuthorDM || isSharedWithUser) &&
    (anonymityLevel === AnonymityLevel.PUBLIC ||
      anonymityLevel === AnonymityLevel.ANONYMIZE_PARTICIPANT_RESULTS)
  ) {
    return false;
  }

  if (isSharing && anonymityLevel !== AnonymityLevel.PUBLIC) {
    return true;
  }

  return (
    anonymityLevel === AnonymityLevel.ANONYMOUS ||
    anonymityLevel === AnonymityLevel.CONFIDENTIAL ||
    (anonymityLevel === AnonymityLevel.ANONYMIZE_PARTICIPANT_RESULTS &&
      !isAuthor)
  );
}

export const isLimitedForWeb = (
  pollType: PollType,
  results: ResultsDeliveryType,
) => {
  return (
    (pollType !== "1to5" &&
      pollType !== "1to10" &&
      pollType !== "fivePoint" &&
      pollType !== "verbalBinary" &&
      pollType !== "nps" &&
      pollType !== "openEnded") ||
    results === ResultsDeliveryType.THREADED
  );
};

export const isOpenForumPolly = (polly: IPollySchema) => {
  return (
    polly.type === "qna" &&
    polly.targets.some(
      (t) =>
        t.__typename === "PollySlackChannelTargetSchema" &&
        t.type === "slack-channel" &&
        t.role === "moderation",
    )
  );
};
